/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { Card, CardBody, Button, Flex, Text, ListItem, UnorderedList } from '@chakra-ui/react';
import { IModal, UploadFileStatus, UserRole } from '../../../../types';
import { Modal } from '../../../atoms/modal';
import MultiFileUpload from '../../../atoms/multi-file-upload';

export interface IDispatchProps {
  onClose: () => void;
  onUploadFile: (openModal: IModal, fileName: string, fileType: string, fileIndex: number, file: File) => void;
  onDeleteFile?: (
    fileName: string,
    sponsorType: string,
    sponsorKey: string,
    docType: string | null,
    docKey: string | null,
    interviewType: string,
    eventType: string,
    eventKey: string,
    interviewHash: string,
    attemptHash: string,
    isFinance: boolean,
    isFollowUp: boolean,
    isInterview: boolean,
    isRecordedInterview: boolean,
    deletedBy?: string,
    shouldShowRequirements?: boolean,
    modalTitle?: string
  ) => void;
  onContinue: (
    sponsorType: string,
    sponsorKey: string,
    docType: string | null,
    docKey: string | null,
    interviewType: string,
    eventType: string,
    eventKey: string,
    interviewHash: string,
    attemptHash: string,
    isFinance: boolean,
    isFollowUp: boolean,
    isInterview: boolean,
    isRecordedInterview: boolean,
    uploadedBy?: string,
    shouldShowRequirements?: boolean,
    modalTitle?: string
  ) => void;
}

export interface IProps {
  uploadFileName?: string;
  uploadFileStatus?: UploadFileStatus;
  uploadFileIndex?: number;
  isFileUploading?: boolean;
  isUploadFailed?: boolean;
  openModal: IModal | null;
  userRole?: UserRole;
}

interface Uploads {
  fileName: string;
  fileStatus: UploadFileStatus;
  progress: number;
}

type Props = IProps & IDispatchProps;

interface IState {
  fileCount: number;
  uploads: Array<Uploads>;
}

const NO_FILE_CHOSEN = 'No file chosen';
class FileUploadModal extends React.Component<Props, IState> {
  interval: number | undefined;
  constructor(props: Props) {
    super(props);
    const { openModal } = props;
    const uploads: Uploads[] =
      openModal && openModal.fileNames
        ? openModal.fileNames.map(fileName => {
            return {
              fileName: fileName,
              fileStatus: UploadFileStatus.OK,
              progress: 100
            };
          })
        : [];
    this.state = {
      fileCount: uploads.length > 1 ? uploads.length : 1,
      uploads: uploads
    };
    this.interval = undefined;
  }

  componentWillReceiveProps(nextProps: IProps) {
    if (
      (nextProps.isFileUploading !== this.props.isFileUploading && !nextProps.isFileUploading) ||
      nextProps.isUploadFailed
    ) {
      this.endAnimation(nextProps.uploadFileIndex || 0, nextProps.uploadFileName || '', nextProps.uploadFileStatus);
    }
  }

  componentWillUnmount() {
    this.interval && clearInterval(this.interval);
  }

  startAnimation = (
    currentfile: number,
    fileName: string,
    progressUpdateValue = 1,
    status: UploadFileStatus = UploadFileStatus.UPLOADING
  ) => {
    this.interval = window.setInterval(() => this.loop(currentfile, fileName, progressUpdateValue, status), 350);
  };

  loop = (
    currentfile: number,
    fileName: string,
    progressUpdateValue = 1,
    status: UploadFileStatus = UploadFileStatus.UPLOADING
  ) => {
    let progressValue = this.state.uploads[currentfile]
      ? this.state.uploads[currentfile].progress + progressUpdateValue
      : progressUpdateValue;

    if (progressValue < 50) {
      if (status === UploadFileStatus.UPLOADING && progressValue > 25 && progressValue <= 30) {
        progressValue -= 0.5;
      } else if (status === UploadFileStatus.UPLOADING && progressValue > 30 && progressValue <= 40) {
        progressValue -= 0.75;
      } else if (status === UploadFileStatus.UPLOADING && progressValue > 40 && progressValue <= 49) {
        progressValue = 20; // re-animate
      }
      this.updateAnimation(currentfile, UploadFileStatus.UPLOADING, fileName, progressValue);
    }
    if (progressValue >= 50) {
      this.interval && clearInterval(this.interval);
      setTimeout(() => {
        this.setState({
          uploads: [
            ...this.state.uploads.slice(0, currentfile),
            {
              fileName: fileName,
              fileStatus: status,
              progress: 100
            },
            ...this.state.uploads.slice(currentfile + 1)
          ]
        });
      }, 500);
    }
  };

  updateAnimation = (
    currentfileIndex: number,
    fileStatus: UploadFileStatus = UploadFileStatus.UPLOADING,
    fileName: string,
    progressValue: number
  ) => {
    this.setState(
      {
        uploads: [
          ...this.state.uploads.slice(0, currentfileIndex),
          {
            fileName: fileName,
            fileStatus: fileStatus,
            progress: progressValue
          },
          ...this.state.uploads.slice(currentfileIndex + 1)
        ]
      },
      this.forceUpdate
    );
  };

  endAnimation = (currentfile: number, fileName: string, status: UploadFileStatus = UploadFileStatus.OK) => {
    this.interval && clearInterval(this.interval);
    this.forceUpdate(); // flush the last setState for progress
    // speed up animation x 10
    this.startAnimation(currentfile, fileName, 3, status);
  };

  getUserRole = (role: UserRole) => {
    return [UserRole.Uni, UserRole.UniAdmin, UserRole.UniLite, UserRole.UniSecure].includes(role)
      ? 'University'
      : role === UserRole.Admin
      ? 'Admin'
      : role === UserRole.Agent
      ? 'Agent'
      : role === UserRole.Enroly
      ? 'Enroly'
      : role === UserRole.AgencyAdmin
      ? 'AgencyAdmin'
      : role === UserRole.ImmigrationProvider
      ? 'ImmigrationProvider'
      : '';
  };

  handleFileUpload = (index: number, fileName: string, file: File) => {
    const currentfile = index;
    this.startAnimation(currentfile, fileName);
    this.props.onUploadFile({ ...this.props.openModal! }, fileName, file.type, index, file);
  };

  handleContinueClick = (_event: any) => {
    const {
      sponsorType = '',
      sponsorKey = '',
      docType,
      docKey,
      interviewType = '',
      eventType = '',
      eventKey = '',
      interviewHash = '',
      attemptHash = '',
      isFinanceDocumentModal,
      isFollowUpDocumentModal,
      isInterviewDocumentModal,
      isRecordedInterviewDocumentModal,
      shouldShowRequirements,
      modalTitle
    } = this.props.openModal!;

    const uploadedBy = this.getUserRole(this.props.userRole ?? UserRole.Unknown);
    this.props.onContinue(
      sponsorType,
      sponsorKey,
      docType || null,
      docKey || null,
      interviewType,
      eventType,
      eventKey,
      interviewHash,
      attemptHash,
      !!isFinanceDocumentModal,
      !!isFollowUpDocumentModal,
      !!isInterviewDocumentModal,
      !!isRecordedInterviewDocumentModal,
      uploadedBy,
      shouldShowRequirements,
      modalTitle
    );
  };

  handleAnotherFileClick = () => {
    if (this.isUploadInProgress() || this.hasPendingFileUpload()) {
      return false;
    }
    return this.setState({
      fileCount: this.state.fileCount + 1,
      uploads: [
        ...this.state.uploads,
        {
          fileName: NO_FILE_CHOSEN,
          fileStatus: UploadFileStatus.AWAITING,
          progress: 0
        }
      ]
    });
  };

  isUploadValid = () => {
    // all uploads complete and at least 1 is successful
    return (
      this.state.uploads.filter(
        (val: Uploads) =>
          (val.progress === 100 || val.progress === 0) &&
          val.fileStatus !== UploadFileStatus.ERROR &&
          val.fileStatus === UploadFileStatus.OK
      ).length > 0
    );
  };

  isUploadInProgress = () => {
    return (
      this.state.uploads.length !== 0 &&
      !(
        this.state.uploads.length > 0 &&
        this.state.uploads.every((val) => val.progress === 100 || val.progress === 0)
      )
    );
  };

  hasPendingFileUpload = () => {
    return this.state.uploads.length && this.state.uploads.some((val) => val.fileName === NO_FILE_CHOSEN);
  };

  deleteFile = (fileName: string): void => {
    this.setState({
      fileCount: this.state.fileCount - 1,
      uploads: this.state.uploads.filter(upload => upload.fileName !== fileName)
    });

    const {
      sponsorType = '',
      sponsorKey = '',
      docType,
      docKey,
      interviewType = '',
      eventType = '',
      eventKey = '',
      interviewHash = '',
      attemptHash = '',
      isFinanceDocumentModal,
      isFollowUpDocumentModal,
      isInterviewDocumentModal,
      isRecordedInterviewDocumentModal,
      shouldShowRequirements,
      modalTitle
    } = this.props.openModal!;

    const deletedBy = this.getUserRole(this.props.userRole ?? UserRole.Unknown);
    this.props.onDeleteFile &&
      this.props.onDeleteFile(
        fileName,
        sponsorType,
        sponsorKey,
        docType || null,
        docKey || null,
        interviewType,
        eventType,
        eventKey,
        interviewHash,
        attemptHash,
        !!isFinanceDocumentModal,
        !!isFollowUpDocumentModal,
        !!isInterviewDocumentModal,
        !!isRecordedInterviewDocumentModal,
        deletedBy,
        shouldShowRequirements,
        modalTitle
      );
  };

  render() {
    return (
      <Modal
        id={'fileUpload'}
        show={true}
        title={`${this.props.openModal?.modalTitle} - Upload`}
        onClose={this.props.onClose}
        hideFooter
      >
        <div id="innerModal">
          <Card variant="filled" mb={6}>
            <CardBody>
              <Text mb={4}>Check your upload meets the follow requirements:</Text>

              <UnorderedList>
                <ListItem>Include all pages associated with this document</ListItem>
                <ListItem>File types: jpg, jpeg, gif, png, bmp &amp; pdf</ListItem>
                <ListItem>Please unzip any files before uploading</ListItem>
                <ListItem>Please do not upload any password-protected or encrypted documents</ListItem>
              </UnorderedList>
            </CardBody>
          </Card>

          <MultiFileUpload
            maxFiles={20}
            fileCount={this.state.fileCount}
            fileNames={this.state.uploads.map(upload => upload.fileName)}
            fileStatuses={this.state.uploads.map(upload => upload.fileStatus)}
            progresses={this.state.uploads.map(upload => upload.progress)}
            onAddAnotherFileClick={this.handleAnotherFileClick}
            uploadInProgress={this.isUploadInProgress()}
            onFileUpload={this.handleFileUpload}
            onDelete={this.props.onDeleteFile ? this.deleteFile : undefined}
          />

          <Flex justifyContent="end">
            <Button
              isDisabled={!(this.state.uploads.length > 0 && this.isUploadValid()) || this.isUploadInProgress()}
              onClick={this.handleContinueClick}
              variant="solid"
              size="lg"
            >
              Save &amp; Continue
            </Button>
          </Flex>
        </div>
      </Modal>
    );
  }
}

export default FileUploadModal;
